import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeActionTypeProduct} from '../doppe-action-type-product/doppe-action-type-product';
import {
    convertDoppeWixProductToProductSettings,
    convertDoppeWixProductToProductSettingsWithDefaultTexts,
    doppeActionTypeProductActionContentSettingsMetadata
} from '../doppe-action-type-product/doppe-action-type-product';
import type {OmitStrict, PartiallyOptional} from '@wix/devzai-utils-common';
import {arrayFlatten, arrayRemoveNullValues, asArray, immerProduce, iterableMapToArray, hideableValueCreateHidden, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {
    DoppeDtoActionAiSpecDeprecated,
    DoppeDtoStoreProduct,
    DoppeListActionSettings,
    DoppeProductsExternalDataSource
} from '../../client-server-common';
import {
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionLayout,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemGetDefaultListItemSettingsMigration,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
    doppeProductsExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';

export namespace DoppeActionTypeProducts {

    export interface ProductSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeProduct.ProductSettings {
        productTitle: string;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ProductSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeProductsExternalDataSource>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
    }

    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        products?: ProductSettings[];
        previewProductButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        productButtonText?: string;
    }

    export type PartialProps = Partial<OmitStrict<ActionSettings, keyof DoppeListActionSettings<ProductSettings>>> & DeprecatedFields & DoppeListActionSettings<ProductSettings & DeprecatedItemFields>;

    export type ActionAiSpec = DoppeDtoActionAiSpecDeprecated<ActionSettings, typeof doppeActionTypeProductsSettingsMetadata, typeof doppeActionTypeProductsListItemSettingsMetadata>;
}

const doppeActionTypeProductsSettingsMetadata = {
    contentSettingsMetadata: {
        ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
    },
    supportsActionCtaButtons: {
        customCtaButtonTypes: {}
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        mainTitle: true,
        mainImage: true
    }
} satisfies DoppeActionType.SettingsMetadata<DoppeActionTypeProducts.ActionSettings>;

const doppeActionTypeProductsListItemSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeProductActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {}
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        description: true,
        couponCode: true,
        ribbon: true,
        priceData: true
    }
} satisfies DoppeActionType.ListItemsSettingsMetadata<DoppeActionTypeProducts.ActionSettings>

export const doppeActionTypeProducts = doppeActionTypeDefineMetadata<DoppeActionTypeProducts.ActionSettings, DoppeActionTypeProducts.PartialProps>({
    id: 'io.bymo.action/products',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            listSearchSettings,
            listItemTemplateCtaButtonsSettings,
            listItemTemplateSectionsSettings,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        return {
            itemShowPrice: true,
            itemShowDate: false,
            previewShowCoupon: true,
            previewShowPrice: true,
            previewShowDescription: true,
            previewShowTitle: true,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                layout: DoppeActionLayout.Column,
                listDataItems: listDataItems.map(product => {

                    const {
                        productButtonText,
                        ctaButton,
                        customSections,
                        customCtaButtons,
                        sectionsOverridesMap,
                        ctaButtonsOverridesMap,
                        ...restProduct
                    } = product;

                    return {
                        productProvider: null,
                        productProviderId: null,
                        ribbon: hideableValueCreateHidden('SALE'),
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: productButtonText,
                            showButton: true
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(product, true),
                        ...doppeListActionListItemWithSectionsMigration(product),
                        ...doppeListActionListItemGetDefaultListItemSettingsMigration(product.id),
                        ...restProduct as PartiallyOptional<DoppeActionTypeProducts.ProductSettings & DoppeActionTypeProducts.DeprecatedItemFields, 'productProvider' | 'productProviderId' | 'ribbon' | 'ctaButton' | 'customCtaButtons' |'customSections' | 'ctaButtonsOverridesMap' | 'sectionsOverridesMap'>
                    }
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(),
            ...doppeListActionGetMainDetailsDefaultSettings({
                defaultTitle: ''
            }),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
            ...restSettings
        }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    actionSettingsMetadata: doppeActionTypeProductsSettingsMetadata,
    actionListItemSettingsMetadata: doppeActionTypeProductsListItemSettingsMetadata,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
            return product.productImage ? asArray(product.productImage) : skip
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        const productImage = actionSettings.listDataItems[0]?.productImage ? asArray(actionSettings.listDataItems[0]?.productImage)[0] ?? null : null;
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, productImage);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeProductsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
                return product.productProvider !== null && product.productProviderId !== null ?
                    doppeExternalProductProviderResolveBusinessEntityReference(
                        product.productProvider,
                        product.productProviderId
                    ) : skip
            });
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveCouponDataFromListItem: listItem => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(listItem.couponCode, ''),
            couponCode: hideableValueGetVisibleValue(listItem.couponCode, ''),
            couponDescription: '',
            couponTitle: hideableValueGetVisibleValue(listItem.title, ''),
        }
    },
    resolveListItems: action => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                title: item.productTitle,
                action: action,
                couponCode: item.couponCode,
                description: item.description,
                image: item.productImage ? asArray(item.productImage)[0] : null,
                itemId: item.id,
                ribbon: item.ribbon,
                url: item.productPageLink,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.productImage) {
                    const images = asArray(item.productImage);
                    item.productImage = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    });
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});

export function convertDoppeWixProductToProductsSettingsWithDefaultTexts(
    product: DoppeDtoStoreProduct,
    pageContentLocales: DoppeAppPageContentLocales,
): DoppeActionTypeProducts.ProductSettings {
    const productSettings = convertDoppeWixProductToProductSettingsWithDefaultTexts({
        product: product,
        pageContentLocales: pageContentLocales
    });

    const {
        ctaButtons,
        sections,
        ...restSettings
    } = productSettings;

    return {
        ...restSettings,
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(product.id),
    }
}

export function convertDoppeWixProductToProductsSettings(
    product: DoppeDtoStoreProduct,
): DoppeActionTypeProducts.ProductSettings {
    const productSettings = convertDoppeWixProductToProductSettings({
        product: product,
    });

    return {
        ...productSettings,
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(product.id),
    }
}